// src/utils/date.js
/**
 * 时间格式化工具
 */

// 时间格式类型枚举
export const FormatType = {
  // 常用格式
  DATE: 'YYYY-MM-DD',
  TIME: 'HH:mm:ss',
  DATETIME: 'YYYY-MM-DD HH:mm:ss',

  // 日期格式
  DATE_CN: 'YYYY年MM月DD日',
  DATE_DOT: 'YYYY.MM.DD',
  DATE_SLASH: 'YYYY/MM/DD',

  // 时间格式
  TIME_12H: 'hh:mm:ss A',
  TIME_24H: 'HH:mm:ss',

  // 完整格式
  FULL: 'YYYY-MM-DD HH:mm:ss SSS',
  FULL_CN: 'YYYY年MM月DD日 HH:mm:ss',
  FULL_CN_12H: 'YYYY年MM月DD日 hh:mm:ss A',

  // 特殊格式
  YEAR_MONTH: 'YYYY年MM',
  MONTH_DAY: 'MM月DD日',
  YEAR: 'YYYY',
  MONTH: 'MM',
  DAY: 'DD',
  HOUR: 'HH',
  MINUTE: 'mm',
  SECOND: 'ss',

  // 相对时间格式
  RELATIVE: 'relative',
  AGO: 'ago',
  FROM_NOW: 'fromNow'
}

// 时间单位常量
const UNITS = {
  YEAR: 31536000,
  MONTH: 2592000,
  WEEK: 604800,
  DAY: 86400,
  HOUR: 3600,
  MINUTE: 60,
  SECOND: 1
}

/**
 * 格式化时间
 * @param {Date|number|string} time - 时间对象、时间戳或时间字符串
 * @param {string} [format=FormatType.DATETIME] - 目标格式
 * @param {Object} [options] - 额外选项
 * @param {boolean} [options.utc=false] - 是否使用UTC时间
 * @param {boolean} [options.strict=false] - 是否严格解析时间
 * @returns {string} 格式化后的时间字符串
 */
export function formatTime(time, format = FormatType.DATETIME, options = {}) {
  const { utc = false, strict = false } = options

  // 处理不同类型的时间输入
  let date
  if (time instanceof Date) {
    date = time
  } else if (typeof time === 'number') {
    // 处理时间戳，考虑毫秒和秒的情况
    date = new Date(time.toString().length === 10 ? time * 1000 : time)
  } else if (typeof time === 'string') {
    // 尝试直接解析字符串
    date = new Date(time)
    // 如果解析失败(NaN)，尝试使用ISO格式解析
    if (isNaN(date.getTime())) {
      date = new Date(time.replace(/-/g, '/'))
    }
  } else {
    throw new Error('Invalid time input: must be Date object, timestamp, or date string')
  }

  // 检查日期是否有效
  if (isNaN(date.getTime())) {
    throw new Error('Invalid date')
  }

  // 格式化映射
  const formatMap = {
    'YYYY': utc ? date.getUTCFullYear() : date.getFullYear(),
    'YY': String(utc ? date.getUTCFullYear() : date.getFullYear()).slice(-2),
    'MM': String((utc ? date.getUTCMonth() : date.getMonth()) + 1).padStart(2, '0'),
    'M': (utc ? date.getUTCMonth() : date.getMonth()) + 1,
    'DD': String(utc ? date.getUTCDate() : date.getDate()).padStart(2, '0'),
    'D': utc ? date.getUTCDate() : date.getDate(),
    'HH': String(utc ? date.getUTCHours() : date.getHours()).padStart(2, '0'),
    'H': utc ? date.getUTCHours() : date.getHours(),
    'hh': String((utc ? date.getUTCHours() : date.getHours()) % 12 || 12).padStart(2, '0'),
    'h': (utc ? date.getUTCHours() : date.getHours()) % 12 || 12,
    'mm': String(utc ? date.getUTCMinutes() : date.getMinutes()).padStart(2, '0'),
    'm': utc ? date.getUTCMinutes() : date.getMinutes(),
    'ss': String(utc ? date.getUTCSeconds() : date.getSeconds()).padStart(2, '0'),
    's': utc ? date.getUTCSeconds() : date.getSeconds(),
    'SSS': String(utc ? date.getUTCMilliseconds() : date.getMilliseconds()).padStart(3, '0'),
    'A': (utc ? date.getUTCHours() : date.getHours()) >= 12 ? 'PM' : 'AM',
    'a': (utc ? date.getUTCHours() : date.getHours()) >= 12 ? 'pm' : 'am'
  }

  // 替换格式字符串
  let formattedTime = format
  for (const [key, value] of Object.entries(formatMap)) {
    formattedTime = formattedTime.replace(new RegExp(key, 'g'), value)
  }

  return formattedTime
}

/**
 * 获取相对时间
 * @param {Date|number|string} time - 时间对象、时间戳或时间字符串
 * @param {string} [format=FormatType.RELATIVE] - 相对时间格式
 * @returns {string} 相对时间字符串
 */
export function getRelativeTime(time, format = FormatType.RELATIVE) {
  // 获取当前时间
  const now = new Date()
  // 获取目标时间
  const target = new Date(time)

  // 计算时间差（秒）
  const diff = (now - target) / 1000

  if (diff < 0) {
    // 未来时间
    if (format === FormatType.FROM_NOW) {
      return '刚刚'
    }
    return formatTime(target, format)
  }

  if (diff < 30) {
    // 30秒内
    if (format === FormatType.AGO) {
      return '刚刚'
    }
    return '刚刚'
  }

  if (diff < UNITS.MINUTE) {
    // 1分钟内
    const seconds = Math.floor(diff)
    if (format === FormatType.AGO) {
      return `${seconds}秒前`
    }
    return `${seconds}秒后`
  }

  if (diff < UNITS.HOUR) {
    // 1小时内
    const minutes = Math.floor(diff / UNITS.MINUTE)
    if (format === FormatType.AGO) {
      return `${minutes}分钟前`
    }
    return `${minutes}分钟后`
  }

  if (diff < UNITS.DAY) {
    // 1天内
    const hours = Math.floor(diff / UNITS.HOUR)
    if (format === FormatType.AGO) {
      return `${hours}小时前`
    }
    return `${hours}小时后`
  }

  if (diff < UNITS.WEEK) {
    // 1周内
    const days = Math.floor(diff / UNITS.DAY)
    if (format === FormatType.AGO) {
      return `${days}天前`
    }
    return `${days}天后`
  }

  if (diff < UNITS.MONTH) {
    // 1个月内
    const weeks = Math.floor(diff / UNITS.WEEK)
    if (format === FormatType.AGO) {
      return `${weeks}周前`
    }
    return `${weeks}周后`
  }

  if (diff < UNITS.YEAR) {
    // 1年内
    const months = Math.floor(diff / UNITS.MONTH)
    if (format === FormatType.AGO) {
      return `${months}个月前`
    }
    return `${months}个月后`
  }

  // 超过1年
  const years = Math.floor(diff / UNITS.YEAR)
  if (format === FormatType.AGO) {
    return `${years}年前`
  }
  return `${years}年后`
}

/**
 * 获取时间范围
 * @param {string} range - 时间范围类型，如 'today', 'yesterday', 'week', 'month', 'year'
 * @param {Object} [options] - 额外选项
 * @param {boolean} [options.utc=false] - 是否使用UTC时间
 * @param {boolean} [options.format=true] - 是否返回格式化后的时间
 * @returns {Object|string} 时间范围对象或格式化后的字符串
 */
export function getTimeRange(range, options = {}) {
  const { utc = false, format = true } = options
  const now = new Date()
  const result = {
    start: null,
    end: null
  }

  switch (range) {
    case 'today':
      result.start = new Date(utc ? now.getUTCFullYear() : now.getFullYear(),
        utc ? now.getUTCMonth() : now.getMonth(),
        utc ? now.getUTCDate() : now.getDate())
      result.end = new Date(result.start.getTime() + 86400000 - 1)
      break

    case 'yesterday':
      const yesterday = new Date(result.start)
      yesterday.setDate(yesterday.getDate() - 1)
      result.start = yesterday
      result.end = new Date(yesterday.getTime() + 86400000 - 1)
      break

    case 'week':
      const dayOfWeek = utc ? now.getUTCDay() : now.getDay()
      const monday = new Date(now)
      monday.setDate(monday.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1))
      result.start = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate())
      result.end = new Date(result.start.getTime() + 604800000 - 1)
      break

    case 'month':
      result.start = new Date(utc ? now.getUTCFullYear() : now.getFullYear(),
        utc ? now.getUTCMonth() : now.getMonth(), 1)
      result.end = new Date(result.start.getFullYear(), result.start.getMonth() + 1, 0, 23, 59, 59, 999)
      break

    case 'year':
      result.start = new Date(utc ? now.getUTCFullYear() : now.getFullYear(), 0, 1)
      result.end = new Date(result.start.getFullYear(), 11, 31, 23, 59, 59, 999)
      break

    default:
      throw new Error(`Invalid time range: ${range}`)
  }

  if (format) {
    return {
      start: formatTime(result.start, FormatType.DATETIME, { utc }),
      end: formatTime(result.end, FormatType.DATETIME, { utc })
    }
  }

  return result
}

/**
 * 时间格式化类
 */
export class DateFormatter {
  constructor(time, options = {}) {
    this.time = time
    this.options = {
      utc: false,
      strict: false,
      ...options
    }
    this.date = new Date(time)
  }

  /**
   * 格式化时间
   * @param {string} format - 格式字符串
   * @returns {string} 格式化后的时间字符串
   */
  format(format = FormatType.DATETIME) {
    return formatTime(this.date, format, this.options)
  }

  /**
   * 获取相对时间
   * @param {string} format - 相对时间格式
   * @returns {string} 相对时间字符串
   */
  relative(format = FormatType.RELATIVE) {
    return getRelativeTime(this.date, format)
  }

  /**
   * 获取时间范围
   * @param {string} range - 时间范围类型
   * @returns {Object} 时间范围对象
   */
  range(range) {
    return getTimeRange(range, this.options)
  }

  /**
   * 获取时间差
   * @param {Date|number|string} other - 另一个时间
   * @returns {number} 时间差（毫秒）
   */
  diff(other) {
    const otherDate = new Date(other)
    return this.date - otherDate
  }

  /**
   * 添加时间
   * @param {number} amount - 数量
   * @param {string} unit - 单位
   * @returns {Date} 新的日期对象
   */
  add(amount, unit) {
    const newDate = new Date(this.date)
    switch (unit) {
      case 'year':
        newDate.setFullYear(newDate.getFullYear() + amount)
        break
      case 'month':
        newDate.setMonth(newDate.getMonth() + amount)
        break
      case 'day':
        newDate.setDate(newDate.getDate() + amount)
        break
      case 'hour':
        newDate.setHours(newDate.getHours() + amount)
        break
      case 'minute':
        newDate.setMinutes(newDate.getMinutes() + amount)
        break
      case 'second':
        newDate.setSeconds(newDate.getSeconds() + amount)
        break
      default:
        throw new Error(`Invalid time unit: ${unit}`)
    }
    return newDate
  }

  /**
   * 是否是今天
   * @returns {boolean}
   */
  isToday() {
    const today = new Date()
    return this.date.toDateString() === today.toDateString()
  }

  /**
   * 是否是昨天
   * @returns {boolean}
   */
  isYesterday() {
    const yesterday = new Date()
    yesterday.setDate(yesterday.getDate() - 1)
    return this.date.toDateString() === yesterday.toDateString()
  }

  /**
   * 是否是同一周
   * @param {Date} other - 另一个日期
   * @returns {boolean}
   */
  isSameWeek(other) {
    const thisWeek = this.getWeekNumber()
    const otherWeek = new Date(other).getWeekNumber()
    return thisWeek === otherWeek
  }

  /**
   * 获取周数
   * @returns {number}
   */
  getWeekNumber() {
    const date = new Date(this.date)
    date.setHours(0, 0, 0, 0)
    date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7)
    const week1 = new Date(date.getFullYear(), 0, 4)
    return 1 + Math.round(((date - week1) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7)
  }
}

/**
 * 创建日期格式化器
 * @param {Date|number|string} time - 时间对象、时间戳或时间字符串
 * @param {Object} [options] - 额外选项
 * @returns {DateFormatter} 日期格式化器实例
 */
export function createDateFormatter(time, options = {}) {
  return new DateFormatter(time, options)
}
